import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import mantisBlackLogo from '../Assets/img/mantisBlackLogo.png';
import sandyBlackLogo from '../Assets/img/sandyBlackLogo.png';
import { brand } from '../config';
import PublicFooter from './PublicFooter';
import { FileText, Building2, Calendar } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <>
      <Helmet>
        <title>Términos y Condiciones - Mantis Business</title>
        <meta
          name="description"
          content="Términos y condiciones de uso de la plataforma Mantis App S.A.S."
        />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafc] text-slate-800 flex flex-col justify-between selection:bg-orange-500/20 selection:text-orange-900">
        {/* Top Sticky Navigation Bar */}
        <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
            <Link to="/" className="flex items-center gap-2 transition-transform duration-200 hover:scale-105">
              <img
                src={brand === 'sandy' ? sandyBlackLogo : mantisBlackLogo}
                alt="Mantis Logo"
                className="h-8 sm:h-9 w-auto object-contain"
              />
            </Link>
          </div>
        </header>

        {/* Main Document Body with Symmetrical Margins */}
        <main className="flex-1 py-8 sm:py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 sm:space-y-8">
            
            {/* Document Hero Header Banner */}
            <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500 rounded-3xl p-6 sm:p-10 lg:p-12 text-white shadow-xl shadow-orange-500/15 relative overflow-hidden text-left">
              {/* Ambient Glows */}
              <div className="absolute top-[-20%] right-[-10%] w-80 h-80 rounded-full bg-white/10 blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-[-20%] left-[-10%] w-80 h-80 rounded-full bg-black/10 blur-3xl pointer-events-none"></div>

              <div className="relative z-10 space-y-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/25 text-white text-xs font-bold tracking-wider uppercase">
                  <FileText className="w-3.5 h-3.5" />
                  <span>Condiciones de Servicio</span>
                </div>

                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight text-white max-w-3xl">
                  TÉRMINOS Y CONDICIONES DEL SERVICIO MANTIS APP S.A.S.
                </h1>

                <div className="flex flex-wrap items-center gap-y-2 gap-x-4 pt-2 text-xs sm:text-sm text-orange-100">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 opacity-80" />
                    <span>Mantis App S.A.S.</span>
                  </div>
                  <div className="hidden sm:inline">&bull;</div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 opacity-80" />
                    <span>Última actualización: 27 de Julio de 2025</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Body Card */}
            <article className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_10px_35px_-5px_rgba(0,0,0,0.04)] p-6 sm:p-10 lg:p-12 space-y-8 text-left text-slate-700 leading-relaxed text-sm sm:text-base">
              
              {/* Section 1 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>1. Aceptación</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Este acuerdo establece los términos y condiciones ("Términos") bajo los cuales <strong>Mantis App S.A.S.</strong> ("Mantis", "nosotros") proporciona acceso y uso de su plataforma tecnológica con agente de inteligencia artificial ("Plataforma") a usted ("Cliente").
                </p>
                <p className="text-slate-600 text-left">
                  Al aceptar utilizar la Plataforma, el Cliente acepta estar vinculado por estos Términos, los cuales se regirán e interpretarán de acuerdo con las leyes de Colombia.
                </p>
              </section>

              {/* Section 2 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>2. Naturaleza de los Servicios</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Mantis pone a disposición del Cliente un agente de inteligencia artificial ("Agente IA") que, en función de la información, parámetros, políticas y presupuestos definidos por el Cliente:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-slate-600 text-xs sm:text-sm">
                  <li>Valida, aprueba o rechaza solicitudes de compra.</li>
                  <li>Ejecuta y controla presupuestos.</li>
                  <li>Asiste en la creación del presupuesto corporativo.</li>
                </ul>
                <p className="text-slate-600 text-left">
                  La Plataforma es una herramienta tecnológica de apoyo y no sustituye el juicio humano ni constituye asesoría financiera, contable o legal. El Cliente es responsable de todas las decisiones y acciones derivadas del uso de la Plataforma y asume los riesgos asociados.
                </p>
                <p className="text-slate-600 text-left">
                  Mantis no realiza servicios financieros ni directos ni indirectos, ni actúa como representante legal o contractual del Cliente frente a terceros. Los actos y transacciones resultantes del uso de la Plataforma son responsabilidad exclusiva del Cliente y, cuando involucren a terceros, estarán sujetos a los términos y condiciones de dichos terceros.
                </p>
              </section>

              {/* Section 3 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>3. Obligaciones del Cliente</span>
                </h2>
                <p className="text-slate-600 text-left">El Cliente se compromete a:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-slate-600 text-xs sm:text-sm">
                  <li>Suministrar de manera completa, detallada y oportuna toda la información necesaria para la correcta operación de la Plataforma.</li>
                  <li>Configurar y mantener actualizados los parámetros de validación, aprobación y control presupuestal.</li>
                  <li>Restringir y controlar el acceso a la Plataforma por parte de usuarios autorizados, preservando la confidencialidad de las credenciales.</li>
                  <li>Verificar y supervisar las decisiones automatizadas cuando sea necesario.</li>
                  <li>Cumplir con las normas legales, fiscales, contables, de contratación y compras aplicables a sus operaciones.</li>
                  <li>Mantener respaldos actualizados de la información procesada en la Plataforma.</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>4. Limitación de Responsabilidad</span>
                </h2>
                <p className="text-slate-600 text-left">Mantis no será responsable por:</p>
                <ul className="list-disc pl-5 space-y-1.5 text-slate-600 text-xs sm:text-sm">
                  <li>Errores, compras indebidas, asignaciones presupuestales erróneas o incumplimientos normativos derivados de datos incompletos, inexactos o desactualizados proporcionados por el Cliente, o de configuraciones internas definidas por este.</li>
                  <li>Daños, pérdidas o consecuencias derivadas de aprobaciones o rechazos automáticos realizados por el Agente IA.</li>
                  <li>Fallas, demoras o errores en servicios de terceros, incluyendo ERP, pasarelas de pago, entidades financieras, proveedores o aliados.</li>
                  <li>Pérdidas indirectas, lucro cesante, daños consecuenciales o reputacionales.</li>
                  <li>Interrupciones o cambios en los Servicios por motivos de fuerza mayor o caso fortuito, tales como desastres naturales, fallas tecnológicas, ciberataques, actos gubernamentales o situaciones imprevistas.</li>
                </ul>
                <p className="text-slate-600 text-left">
                  La responsabilidad máxima de Mantis se limitará al valor efectivamente pagado por el Cliente en los tres (3) meses anteriores al evento que originó la reclamación.
                </p>
              </section>

              {/* Section 5 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>5. Disponibilidad y Alcance del Servicio</span>
                </h2>
                <p className="text-slate-600 text-left">
                  La Plataforma se presta "tal cual" y "según disponibilidad". Mantis podrá realizar actualizaciones, mantenimientos o mejoras sin previo aviso, procurando minimizar las interrupciones. El Cliente reconoce que la disponibilidad ininterrumpida no está garantizada.
                </p>
              </section>

              {/* Section 6 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>6. Acuerdo de Nivel de Servicio (SLA)</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Mantis prestará atención básica de soporte en horario de oficina, de lunes a viernes de 8:00 a.m. a 6:00 p.m. (hora Colombia), excluyendo festivos nacionales.
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-slate-600 text-xs sm:text-sm">
                  <li>Recepción y registro de incidentes reportados por el Cliente.</li>
                  <li>Orientación inicial para resolver problemas de uso de la Plataforma.</li>
                  <li>Escalamiento de incidencias técnicas al equipo correspondiente.</li>
                </ul>
                <p className="text-slate-600 text-left">
                  El tiempo de respuesta inicial a un reporte será de máximo un (1) día hábil. El tiempo de resolución dependerá de la complejidad del incidente y de factores fuera del control de Mantis.
                </p>
              </section>

              {/* Section 7 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>7. Propiedad Intelectual</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Todos los derechos de propiedad intelectual sobre la Plataforma, el Agente IA, su código, modelos, algoritmos, interfaces y documentación pertenecen exclusivamente a Mantis. El Cliente recibe una licencia no exclusiva, intransferible y revocable para el uso de la Plataforma conforme a estos Términos.
                </p>
              </section>

              {/* Section 8 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>8. Integraciones con Terceros</span>
                </h2>
                <p className="text-slate-600 text-left">
                  En caso de integrarse la Plataforma con sistemas externos (ERP, pasarelas de pago, bases de datos, plataformas de terceros), Mantis no garantiza el funcionamiento ininterrumpido ni asume responsabilidad por fallas de terceros. El Cliente será responsable de los contratos y configuraciones necesarias con dichos terceros.
                </p>
              </section>

              {/* Section 9 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>9. Seguridad y Confidencialidad</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Mantis adoptará medidas razonables para proteger la información proporcionada por el Cliente. La información del Cliente será tratada como confidencial y solo se compartirá con terceros en los casos necesarios para la prestación del servicio o por requerimiento legal.
                </p>
              </section>

              {/* Section 10 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>10. Cambios en los Términos</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Mantis podrá modificar estos Términos en cualquier momento. Si se modifican, Mantis informará al Cliente a través de la Plataforma o por medios electrónicos, y de no existir cancelación del servicio dentro de los tres (3) días siguientes a su publicación, se entenderán aceptados por el Cliente.
                </p>
              </section>

              {/* Section 11 */}
              <section className="space-y-3">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2.5 pb-2 border-b border-slate-100">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                  <span>11. Ley Aplicable y Jurisdicción</span>
                </h2>
                <p className="text-slate-600 text-left">
                  Estos Términos se regirán por las leyes de la República de Colombia. Cualquier disputa, controversia o reclamación derivada de estos Términos será resuelta por la jurisdicción competente de los jueces de la ciudad de Bogotá D.C., Colombia.
                </p>
              </section>

              {/* Exoneración */}
              <section className="space-y-3 pt-4 border-t border-slate-200/80">
                <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-5 space-y-2 text-amber-950">
                  <h3 className="font-bold text-sm sm:text-base">Exoneración Específica</h3>
                  <p className="text-xs sm:text-sm text-amber-900/90 leading-relaxed">
                    El Cliente reconoce y acepta expresamente que el uso de la Plataforma y del Agente IA se realiza bajo su exclusiva responsabilidad. Las decisiones generadas son asistencias tecnológicas y no constituyen instrucciones vinculantes. Cualquier compra indebida, ejecución presupuestal incorrecta o pérdida económica resultante será responsabilidad exclusiva del Cliente.
                  </p>
                </div>
              </section>
            </article>
          </div>
        </main>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
};

export default TermsAndConditions;
